import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import * as authService from '../services/auth.service';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');

  const navigate = useNavigate();
  const { login } = useAuth();

  // Validate password strength
  function validatePassword(pwd) {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain an uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain a lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain a number';
    }
    return null;
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const result = await authService.signUp(email, password, givenName, familyName);

      if (result.userConfirmed) {
        // User auto-confirmed (shouldn't happen with email verification)
        await login(email, password);
        navigate('/account');
      } else {
        // Need email verification
        setNeedsConfirmation(true);
      }
    } catch (err) {
      let errorMessage = 'Failed to sign up';

      if (err.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists.';
      } else if (err.code === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements.';
      } else if (err.code === 'InvalidParameterException') {
        errorMessage = 'Invalid email or password format.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmation(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.confirmSignUp(email, confirmationCode);

      // Auto-login after confirmation
      await login(email, password);
      navigate('/account');
    } catch (err) {
      let errorMessage = 'Failed to verify code';

      if (err.code === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code. Please try again.';
      } else if (err.code === 'ExpiredCodeException') {
        errorMessage = 'Verification code expired. Request a new one.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function resendCode() {
    setError('');
    setLoading(true);

    try {
      await authService.resendConfirmationCode(email);
      setError(''); // Clear any errors
      alert('Verification code sent! Check your email.');
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  }

  // Email verification screen
  if (needsConfirmation) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="bg-logo-pink text-white py-5">
          <div className="max-w-7xl mx-auto px-4">
            <Link to="/" className="font-logo text-3xl tracking-wide hover:opacity-90 transition">
              Make Merry
            </Link>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Verify your email</h2>
              <p className="mt-2 text-gray-600">
                We sent a verification code to <strong>{email}</strong>
              </p>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleConfirmation}>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  required
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-logo-pink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-logo-pink disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={loading}
                  className="text-sm text-logo-pink hover:opacity-80 transition disabled:opacity-50"
                >
                  Resend verification code
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Signup form
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="bg-logo-pink text-white py-5">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/" className="font-logo text-3xl tracking-wide hover:opacity-90 transition">
            Make Merry
          </Link>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
            <p className="mt-2 text-gray-600">
              Or{' '}
              <Link
                to="/login"
                className="font-medium text-logo-pink hover:opacity-80 transition"
              >
                sign in to existing account
              </Link>
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 p-4 border border-red-200">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSignup}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="givenName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    id="givenName"
                    name="givenName"
                    type="text"
                    value={givenName}
                    onChange={(e) => setGivenName(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label htmlFor="familyName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    id="familyName"
                    name="familyName"
                    type="text"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                  placeholder="Create a password"
                />
                <p className="mt-1 text-xs text-gray-500">
                  At least 8 characters with uppercase, lowercase, and number
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                  placeholder="Confirm your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-logo-pink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-logo-pink disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? 'Creating account...' : 'Sign up'}
            </button>
          </form>

          <div className="text-center pt-4">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 transition">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
